'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OrgMemberRole = 'owner' | 'admin' | 'employee' | 'client';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: string;
  is_active: boolean;
  settings?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface OrgMembership {
  organization_id: string;
  role: OrgMemberRole;
  is_active: boolean;
  joined_at: string;
}

interface OrganizationContextValue {
  /** The currently active organization */
  currentOrg: Organization | null;
  /** The current user's role within the current organization */
  orgRole: OrgMemberRole | null;
  /** All organizations the user belongs to */
  organizations: Organization[];
  /** True while org data is loading */
  loading: boolean;
  /** Switch the active organization (for future multi-org support) */
  switchOrganization: (orgId: string) => Promise<void>;
  /** Refresh org data from the database */
  refreshOrg: () => Promise<void>;
  // ─── Permission helpers (org-scoped) ──────────────────────────────────────
  /** True if the user is owner or admin of the current org */
  isOrgAdmin: () => boolean;
  /** True if the user is owner of the current org */
  isOrgOwner: () => boolean;
  /** True if the user is an employee (or higher) of the current org */
  isOrgEmployee: () => boolean;
  /** True if the user is a client of the current org */
  isOrgClient: () => boolean;
  /** True if the user has any active membership in the current org */
  isOrgMember: () => boolean;
  /** The current organization's ID — convenience accessor */
  orgId: string | null;
}

const OrganizationContext = createContext<OrganizationContextValue>({} as OrganizationContextValue);

export const useOrganization = () => {
  const ctx = useContext(OrganizationContext);
  if (!ctx) throw new Error('useOrganization must be used within OrganizationProvider');
  return ctx;
};

// ─── Provider ────────────────────────────────────────────────────────────────

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [orgRole, setOrgRole] = useState<OrgMemberRole | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const loadOrgData = useCallback(async () => {
    if (!user) {
      setCurrentOrg(null);
      setOrgRole(null);
      setOrganizations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch all org memberships for the current user
      const { data: memberships, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id, role, is_active, joined_at, organizations(id, name, slug, plan, is_active, settings, created_at, updated_at)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('joined_at', { ascending: true });

      if (memberError || !memberships || memberships.length === 0) {
        // No org membership found — set null and let middleware redirect to /create-organization.
        // Never fall back to loading another organization's data.
        setCurrentOrg(null);
        setOrgRole(null);
        setOrganizations([]);
        setLoading(false);
        return;
      }

      type MembershipRow = {
  organization_id: string;
  role: OrgMemberRole;
  is_active: boolean;
  joined_at: string;
  organizations: Organization[];
};

      const orgs: Organization[] = (memberships as unknown as MembershipRow[])
  .flatMap((m) => m.organizations);

      setOrganizations(orgs);

      // Use the first (earliest joined) org as the active org
      const firstMembership = memberships[0] as unknown as MembershipRow;
const firstOrg = firstMembership.organizations[0] ?? null;

      if (firstOrg) {
        setCurrentOrg(firstOrg);
        setOrgRole(firstMembership.role);
      } else {
        setCurrentOrg(null);
        setOrgRole(null);
      }
    } catch {
      // Non-critical — silently degrade
      setCurrentOrg(null);
      setOrgRole(null);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (!authLoading) {
      loadOrgData();
    }
  }, [authLoading, loadOrgData]);

  const switchOrganization = async (orgId: string) => {
    const target = organizations.find((o) => o.id === orgId);
    if (!target || !user) return;

    // Fetch the user's role in the target org
    const { data: membership } = await supabase
      .from('organization_members')
      .select('role')
      .eq('organization_id', orgId)
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();

    setCurrentOrg(target);
    setOrgRole((membership?.role as OrgMemberRole) ?? 'employee');
  };

  const refreshOrg = async () => {
    await loadOrgData();
  };

  // ─── Permission helpers ───────────────────────────────────────────────────

  const isOrgOwner = () => orgRole === 'owner';
  const isOrgAdmin = () => orgRole === 'owner' || orgRole === 'admin';
  const isOrgEmployee = () => orgRole === 'owner' || orgRole === 'admin' || orgRole === 'employee';
  const isOrgClient = () => orgRole === 'client';
  const isOrgMember = () => orgRole !== null;

  const value: OrganizationContextValue = {
    currentOrg,
    orgRole,
    organizations,
    loading,
    switchOrganization,
    refreshOrg,
    isOrgAdmin,
    isOrgOwner,
    isOrgEmployee,
    isOrgClient,
    isOrgMember,
    orgId: currentOrg?.id ?? null,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};
